/**
 * Heuristic yarn-weight classifier from label inputs.
 *
 * Veronika 2026-05-05: she wanted the weight pill on the Add Yarn
 * form to fill itself in once the user typed length + grams + needle
 * size, with manual override for the edge cases (superwash bias,
 * bouclé, chainette, hand-spun) where the heuristic lies.
 *
 * Two signals with a strict priority order:
 *
 *   1. **meters per 100g** — the truthful thickness signal. If
 *      grams + meters are both present, m/100g alone decides.
 *   2. **recommended needle size** — fallback only when m/100g is
 *      missing. Range strings ("4mm - 5mm") are read as the upper
 *      bound.
 *
 * Earlier (build 1030) the two signals were combined with a "pick
 * the heavier" tiebreak. That misclassified Lamana Como Tweed (25g
 * / 120m → 480 m/100g, true Fingering) as Worsted because the label
 * recommends 4–5 mm needles for an airy drape. Needle recommendations
 * are stylistic — the same Fingering yarn knits on 3–4 mm for socks
 * or 5 mm for a lacy shawl. m/100g is the only direct measurement
 * of actual yarn thickness, so we trust it absolutely when present.
 *
 * Returns null when there isn't enough info to guess. Caller should
 * leave the user's existing pick untouched in that case.
 */

export type YarnWeightClass =
  | 'Lace' | 'Fingering' | 'Sport' | 'DK'
  | 'Worsted' | 'Aran' | 'Bulky' | 'Super Bulky';

const WEIGHT_RANK: Record<YarnWeightClass, number> = {
  Lace: 0, Fingering: 1, Sport: 2, DK: 3,
  Worsted: 4, Aran: 5, Bulky: 6, 'Super Bulky': 7,
};

/**
 * Classify by manufacturer-stated knit gauge — stitches per 10 cm.
 * This is the truthful "thickness" signal: gauge measures how wide
 * a stitch actually sits, after the yarn's loft/halo/twist structure
 * is taken into account. Fluffy fine yarns (tweed, mohair, brushed)
 * have a Fingering core but knit at Sport gauge because the halo
 * fills the gap — m/100g would mislabel them as Lace, gauge gets
 * them right.
 *
 * Bands centered on Veronika's reference card (2026-05-05) and the
 * Craft Yarn Council standards. The 22-sts boundary calls Sport
 * (rather than DK) because that's the European convention she's
 * working from.
 */
function fromGaugeStitches(sts: number): YarnWeightClass | null {
  if (!isFinite(sts) || sts <= 0) return null;
  // Bands match Veronika's authoritative table (2026-05-05):
  //   Lace 32-40, Fingering 26-30, Sport 24-26, DK 21-23,
  //   Worsted 18-20, Aran 16-18, Bulky 12-15, Super Bulky 7-11.
  // Boundaries that fall in two bands resolve to the finer weight
  // (more sts/10cm = thinner yarn) — that matches the "tighter knit
  // wins" reading knitters apply by default.
  if (sts >= 32) return 'Lace';
  if (sts >= 26) return 'Fingering';
  if (sts >= 24) return 'Sport';
  if (sts >= 21) return 'DK';
  if (sts >= 18) return 'Worsted';
  if (sts >= 16) return 'Aran';
  if (sts >= 12) return 'Bulky';
  return 'Super Bulky';
}

function fromMetersPer100g(m100: number): YarnWeightClass | null {
  if (!isFinite(m100) || m100 <= 0) return null;
  // Bands match Veronika's authoritative table (2026-05-05):
  //   Lace 600-800, Fingering 350-420, Sport 280-320, DK 200-250,
  //   Worsted 160-200, Aran 120-150, Bulky 80-120, Super Bulky <80.
  // Boundaries between bands resolve to the heavier weight (longer
  // m/100g already implied finer; the threshold puts the cutoff at
  // the band boundary so 200 = DK, 199 = Worsted, etc.).
  if (m100 >= 510) return 'Lace';
  if (m100 >= 335) return 'Fingering';
  if (m100 >= 265) return 'Sport';
  if (m100 >= 200) return 'DK';
  if (m100 >= 155) return 'Worsted';
  if (m100 >= 120) return 'Aran';
  if (m100 >= 80) return 'Bulky';
  return 'Super Bulky';
}

/** Pull the maximum needle mm value out of a free-text string.
 *  Handles "4mm - 5mm", "4-5mm", "3,5-4,5" (European comma decimals),
 *  "US 7" (still ignored — units aren't translated). The first
 *  normalization step replaces commas with dots so "3,5" parses as
 *  3.5 instead of two separate ints. */
function maxNeedleMm(needles: string): number | null {
  const normalized = needles.replace(/,/g, '.');
  const numbers = normalized.match(/\d+(?:\.\d+)?/g);
  if (!numbers || numbers.length === 0) return null;
  const upper = Math.max(...numbers.map(Number).filter(n => isFinite(n) && n < 30));
  if (!isFinite(upper) || upper <= 0) return null;
  return upper;
}

function fromNeedleString(needles: string | undefined): YarnWeightClass | null {
  if (!needles) return null;
  const upper = maxNeedleMm(needles);
  if (upper == null) return null;
  // Bands match Veronika's authoritative table (2026-05-05):
  //   Lace 1.5-2.5, Fingering 2-3, Sport 3-3.5, DK 3.5-4,
  //   Worsted 4.5-5, Aran 5-6, Bulky 6-8, Super Bulky 8-12 (mm).
  if (upper < 2) return 'Lace';
  if (upper < 3) return 'Fingering';
  if (upper < 3.5) return 'Sport';
  if (upper < 4.5) return 'DK';
  if (upper < 5) return 'Worsted';
  if (upper < 6) return 'Aran';
  if (upper < 8) return 'Bulky';
  return 'Super Bulky';
}

export interface ClassifyInputs {
  /** Meters per skein. */
  metersPerSkein: number;
  /** Grams per skein. */
  gramsPerSkein: number;
  /** Manufacturer's stated gauge — stitches per 10 cm. The most
   *  truthful signal because it accounts for halo/loft. */
  gaugeStitches?: number;
  /** Optional upper bound when the manufacturer prints a gauge
   *  range like "22-24 sts/10cm". Classifier prefers this over
   *  `gaugeStitches` because the upper end of the range puts borderline
   *  yarns (22-24 → Sport, not DK) into the band knitters actually
   *  use them at. */
  gaugeStitchesMax?: number;
  /** Free-text needle range from the label, e.g. "4mm - 5mm". */
  recommendedNeedles?: string;
}

/**
 * Strict priority order:
 *   1. gauge stitches per 10cm (when set, > 0)
 *   2. meters per 100g  (when grams + meters set)
 *   3. recommended needle size (last-resort fallback)
 *
 * Each signal is consulted in turn; the first one that classifies
 * wins. Earlier builds combined m/100g and needles with a "heavier
 * wins" tiebreak — that misclassified Lamana Como Tweed (Fingering
 * core, Sport-gauge halo, 4-5mm "airy drape" needles) as Worsted.
 * Gauge solves the halo problem because it measures actual stitch
 * width on the recommended needle, not the spun-core thickness.
 */
export function classifyYarnWeight(inputs: ClassifyInputs): YarnWeightClass | null {
  // Prefer the max of the gauge range — for a yarn labeled 22-24
  // sts/10cm, 24 is the band that knitters loosen toward, and that's
  // the one we want to classify against (Sport, not DK).
  const gaugeForClassify = Math.max(
    inputs.gaugeStitchesMax || 0,
    inputs.gaugeStitches || 0,
  );
  const gaugeGuess = fromGaugeStitches(gaugeForClassify);
  if (gaugeGuess) return gaugeGuess;
  const haveLengthGrams = inputs.metersPerSkein > 0 && inputs.gramsPerSkein > 0;
  if (haveLengthGrams) {
    const m100 = (inputs.metersPerSkein / inputs.gramsPerSkein) * 100;
    const lengthGuess = fromMetersPer100g(m100);
    if (lengthGuess) return lengthGuess;
  }
  return fromNeedleString(inputs.recommendedNeedles);
}

/**
 * Build 1030's classifier — kept verbatim (with the original thresholds
 * and the old comma-blind needle parser) so the v2 weight-backfill
 * (build 1033) can detect rows that v1 actually wrote and re-classify
 * those, without trampling manual picks.
 *
 * IMPORTANT: do not "fix" or update the thresholds here. The function's
 * job is to reproduce v1's exact output for any given input — if it
 * drifts, the backfill mis-identifies which rows are user-edited and
 * either trampling manual picks or skipping rows that should update.
 */
function fromMetersPer100gLegacy(m100: number): YarnWeightClass | null {
  if (!isFinite(m100) || m100 <= 0) return null;
  if (m100 >= 500) return 'Lace';
  if (m100 >= 350) return 'Fingering';
  if (m100 >= 270) return 'Sport';
  if (m100 >= 210) return 'DK';
  if (m100 >= 165) return 'Worsted';
  if (m100 >= 130) return 'Aran';
  if (m100 >= 90) return 'Bulky';
  return 'Super Bulky';
}

function fromNeedleStringLegacy(needles: string | undefined): YarnWeightClass | null {
  if (!needles) return null;
  // v1's parser was comma-blind ("3,5" → ["3","5"]). Reproduced here
  // so the legacy detector matches what v1 wrote for European
  // comma-decimal labels.
  const numbers = needles.match(/\d+(?:\.\d+)?/g);
  if (!numbers || numbers.length === 0) return null;
  const upper = Math.max(...numbers.map(Number).filter(n => isFinite(n) && n < 30));
  if (!isFinite(upper) || upper <= 0) return null;
  if (upper < 2.5) return 'Lace';
  if (upper < 3.25) return 'Fingering';
  if (upper < 3.75) return 'Sport';
  if (upper < 4.5) return 'DK';
  if (upper < 5.25) return 'Worsted';
  if (upper < 6) return 'Aran';
  if (upper < 7.5) return 'Bulky';
  return 'Super Bulky';
}

export function classifyYarnWeightLegacy(inputs: Omit<ClassifyInputs, 'gaugeStitches'>): YarnWeightClass | null {
  const { metersPerSkein, gramsPerSkein, recommendedNeedles } = inputs;
  const haveLengthGrams = metersPerSkein > 0 && gramsPerSkein > 0;
  if (!haveLengthGrams) return null;
  const m100 = (metersPerSkein / gramsPerSkein) * 100;
  const lengthGuess = fromMetersPer100gLegacy(m100);
  const needleGuess = fromNeedleStringLegacy(recommendedNeedles);
  if (!lengthGuess) return needleGuess;
  if (!needleGuess) return lengthGuess;
  return WEIGHT_RANK[lengthGuess] >= WEIGHT_RANK[needleGuess] ? lengthGuess : needleGuess;
}
